// ===================================================================
// VolunteerOS -- Check-In / Check-Out
// Shift code entry, GPS capture, time-gated check-in, elapsed time
// ===================================================================

import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Table, Select,
} from '../../components/gab/GabComponents';
import {
  IconLogin, IconLogout, IconMapPin, IconClock, IconBarcode,
  IconRefresh, IconSearch, IconUsers, IconX,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import dayjs from 'dayjs';
import {
  useVolunteerStore,
  type VolAssignment,
  type Volunteer,
  type VolEvent,
  mergeExtras,
  parseExtras,
} from '../../store/volunteerStore';
import { useAuthStore } from '../../store/authStore';
import { semantic } from '../../lib/colors';
import { useModulePermission } from '../../hooks/useModulePermission';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

// ── helpers ──────────────────────────────────────────────────────

function fmtTime(iso: string): string {
  if (!iso) return '--';
  return dayjs(iso).format('h:mm A');
}

function elapsedLabel(checkInIso: string): string {
  const mins = dayjs().diff(dayjs(checkInIso), 'minute');
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m`;
}

function hoursWorked(checkInIso: string, checkOutIso: string): string {
  const diff = dayjs(checkOutIso).diff(dayjs(checkInIso), 'minute');
  return (diff / 60).toFixed(2);
}

function isToday(dateStr: string): boolean {
  if (!dateStr) return false;
  return dayjs(dateStr).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
}

function withinCheckInWindow(shiftDate: string, startTime: string): boolean {
  if (!shiftDate) return true; // no date = allow
  const shiftStart = dayjs(
    `${dayjs(shiftDate).format('YYYY-MM-DD')} ${startTime || '00:00'}`,
  );
  const now = dayjs();
  // Allow check-in up to 1 hour before shift start, and any time after
  return now.isAfter(shiftStart.subtract(1, 'hour'));
}

// ── component ────────────────────────────────────────────────────

export default function CheckIn() {
  const { canEdit } = useModulePermission('volunteer');
  const incidents = useAuthStore(s => s.incidents);
  const incidentMap = useMemo(() =>
    new Map(incidents.map(i => [i.id, i.name])),
    [incidents]
  );
  const {
    volunteers, events, assignments,
    loadVolunteers, loadEvents, loadAssignments,
    updateAssignment, createAssignment,
  } = useVolunteerStore();

  const [shiftCode, setShiftCode] = useState('');
  const [filterEventId, setFilterEventId] = useState<string | null>(null);
  const [filterEventName, setFilterEventName] = useState<string>('');
  const [geoLocation, setGeoLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<number | null>(null);
  const initialLoad = useRef(false);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [, setTick] = useState(0); // force re-render for elapsed time

  // Walk-in check-in state
  const [walkInQuery, setWalkInQuery] = useState('');
  const [walkInResults, setWalkInResults] = useState<Array<{ id: number; firstName: string; lastName: string; email: string }>>([]);
  const [walkInSearching, setWalkInSearching] = useState(false);
  const [walkInPersonId, setWalkInPersonId] = useState<number | null>(null);
  const [walkInPersonName, setWalkInPersonName] = useState('');
  const [walkInEventId, setWalkInEventId] = useState<string | null>(null);
  const [walkInProcessing, setWalkInProcessing] = useState(false);
  const walkInDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── initial data load ──────────────────────────────────────────
  useEffect(() => {
    if (initialLoad.current) return;
    initialLoad.current = true;
    loadVolunteers();
    loadEvents();
    loadAssignments();
  }, [loadVolunteers, loadEvents, loadAssignments]);

  // ── tick every 30s for elapsed time display ────────────────────
  useEffect(() => {
    tickRef.current = setInterval(() => setTick(t => t + 1), 30_000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, []);

  // Load settings for GPS enforcement
  const volSettings = useMemo(() => {
    try {
      const raw = localStorage.getItem('vol-org-settings');
      if (raw) return JSON.parse(raw);
    } catch { /* */ }
    return { checkIn: { requireGeo: false, selfCheckin: true } };
  }, []);
  const gpsRequired = volSettings.checkIn?.requireGeo || false;

  // ── GPS ────────────────────────────────────────────────────────
  const acquireGps = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError(gpsRequired ? 'GPS required but not available — check-in blocked' : 'GPS not available — check-in will proceed without location');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoError(null);
      },
      () => setGeoError(gpsRequired ? 'GPS required — enable location services to check in' : 'GPS unavailable — location is optional'),
      { enableHighAccuracy: true, timeout: 5000 },
    );
  }, [gpsRequired]);

  useEffect(() => { acquireGps(); }, [acquireGps]);

  // ── lookup maps ────────────────────────────────────────────────
  const volMap = useMemo(() => {
    const m = new Map<string, Volunteer>();
    volunteers.forEach((v) => m.set(String(v.id), v));
    return m;
  }, [volunteers]);

  const eventMap = useMemo(() => {
    const m = new Map<string, VolEvent>();
    events.forEach((e) => m.set(String(e.id), e));
    return m;
  }, [events]);

  // ── derived lists ──────────────────────────────────────────────
  const readyToCheckIn = useMemo(() => {
    return assignments.filter((a) => {
      if (a.checkIn) return false;
      if (a.status !== 'confirmed' && a.status !== 'scheduled') return false;
      if (filterEventId) return String(a.eventId) === filterEventId;
      return isToday(a.shiftDate);
    });
  }, [assignments, filterEventId]);

  const currentlyCheckedIn = useMemo(() => {
    return assignments.filter((a) => !!a.checkIn && !a.checkOut);
  }, [assignments]);

  const recentActivity = useMemo(() => {
    return assignments
      .filter((a) => a.checkIn && isToday(a.checkIn))
      .sort((a, b) => (b.checkIn || '').localeCompare(a.checkIn || ''));
  }, [assignments]);

  // ── actions ────────────────────────────────────────────────────
  const handleCheckIn = async (a: VolAssignment) => {
    // Block if GPS required but not acquired
    if (gpsRequired && !geoLocation) {
      notifications.show({ title: 'GPS Required', message: 'Enable location services before checking in.', color: 'var(--color-error)' });
      acquireGps();
      return;
    }
    setProcessing(a.id);
    try {
      const updates: Partial<VolAssignment> = {
        checkIn: dayjs().toISOString(),
      };
      if (geoLocation) {
        updates.notes = mergeExtras(a.notes, {
          checkInLat: geoLocation.lat,
          checkInLng: geoLocation.lng,
        });
      }
      await updateAssignment(a.id, updates);
      const vol = volMap.get(String(a.volunteerId));
      const name = vol ? `${vol.firstName} ${vol.lastName}` : `Volunteer #${a.volunteerId}`;
      notifications.show({ message: `${name} checked in`, color: 'var(--color-success)' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Check-in failed';
      notifications.show({ message: msg, color: 'var(--color-error)' });
    } finally {
      setProcessing(null);
    }
  };

  const handleCheckOut = async (a: VolAssignment) => {
    setProcessing(a.id);
    try {
      const now = dayjs().toISOString();
      const hrs = hoursWorked(a.checkIn, now);
      const updates: Partial<VolAssignment> = {
        checkOut: now,
        hours: hrs,
        status: 'completed',
      };
      if (geoLocation) {
        updates.notes = mergeExtras(a.notes, {
          checkOutLat: geoLocation.lat,
          checkOutLng: geoLocation.lng,
        });
      }
      await updateAssignment(a.id, updates);
      const vol = volMap.get(String(a.volunteerId));
      const name = vol ? `${vol.firstName} ${vol.lastName}` : `Volunteer #${a.volunteerId}`;
      notifications.show({ message: `${name} checked out (${hrs} hrs)`, color: 'var(--color-primary-700)' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Check-out failed';
      notifications.show({ message: msg, color: 'var(--color-error)' });
    } finally {
      setProcessing(null);
    }
  };

  // ── walk-in directory search ───────────────────────────────────
  const handleWalkInSearch = (q: string) => {
    setWalkInQuery(q);
    if (walkInDebounce.current) clearTimeout(walkInDebounce.current);
    if (!q.trim()) { setWalkInResults([]); return; }
    walkInDebounce.current = setTimeout(async () => {
      setWalkInSearching(true);
      try {
        const FP = FIELDS.people;
        const res = await queryRecords<Record<string, unknown>>(TABLES.people, {
          filters: [
            { field: FP.type, operator: 'eq', value: 'volunteer' },
            { field: FP.status, operator: 'eq', value: 'active' },
          ],
          limit: 10,
        });
        const lower = q.toLowerCase();
        const matched = res.records
          .map(r => ({
            id: r.record_id as number,
            firstName: String(r[FP.firstName] ?? ''),
            lastName: String(r[FP.lastName] ?? ''),
            email: String(r[FP.email] ?? ''),
          }))
          .filter(p => {
            const name = `${p.firstName} ${p.lastName}`.toLowerCase();
            return name.includes(lower) || p.email.toLowerCase().includes(lower);
          });
        setWalkInResults(matched);
      } catch { /* */ }
      setWalkInSearching(false);
    }, 300);
  };

  const handleWalkIn = async () => {
    if (!walkInPersonId || !walkInEventId) {
      notifications.show({ message: 'Select a volunteer and event', color: '#C68700' });
      return;
    }
    setWalkInProcessing(true);
    try {
      await createAssignment({
        eventId: Number(walkInEventId),
        volunteerName: walkInPersonName,
        status: 'confirmed',
        checkIn: dayjs().toISOString(),
        shiftDate: dayjs().format('YYYY-MM-DD'),
        personId: String(walkInPersonId),
        notes: JSON.stringify({
          volunteerName: walkInPersonName,
          ...(geoLocation ? { checkInLat: geoLocation.lat, checkInLng: geoLocation.lng } : {}),
        }),
      });
      notifications.show({ message: `${walkInPersonName} walked in and checked in`, color: 'var(--color-success)' });
      setWalkInPersonId(null);
      setWalkInPersonName('');
      setWalkInQuery('');
      setWalkInResults([]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Walk-in check-in failed';
      notifications.show({ message: msg, color: 'var(--color-error)' });
    } finally {
      setWalkInProcessing(false);
    }
  };

  // ── shift-code lookup ──────────────────────────────────────────
  const handleCodeSubmit = () => {
    if (!shiftCode.trim()) {
      notifications.show({ message: 'Enter a shift code', color: '#C68700' });
      return;
    }
    const code = shiftCode.trim().toUpperCase();
    const matchedEvent = events.find(
      (e) => (e.checkInCode || '').toUpperCase() === code,
    );
    if (!matchedEvent) {
      notifications.show({ message: 'No event found for that code', color: 'var(--color-error)' });
      setShiftCode('');
      return;
    }
    // Find assignments for this event that are ready
    const eventAssignments = assignments.filter(
      (a) =>
        String(a.eventId) === String(matchedEvent.id) &&
        !a.checkIn &&
        (a.status === 'confirmed' || a.status === 'scheduled'),
    );
    const name = matchedEvent.name || matchedEvent.eventName || `Event #${matchedEvent.id}`;
    if (eventAssignments.length === 0) {
      notifications.show({
        message: `Event "${name}" found but no pending assignments`,
        color: '#C68700',
      });
    } else {
      setFilterEventId(String(matchedEvent.id));
      setFilterEventName(name);
    }
    setShiftCode('');
  };

  // ── helper to resolve names ────────────────────────────────────
  const volName = (a: VolAssignment): string => {
    const v = volMap.get(String(a.volunteerId));
    if (v) return `${v.firstName} ${v.lastName}`;
    const extras = parseExtras(a.notes);
    if (extras.volunteerName) return String(extras.volunteerName);
    return `Vol #${a.volunteerId}`;
  };

  const eventName = (a: VolAssignment): string => {
    const e = eventMap.get(String(a.eventId));
    return e ? (e.name || e.eventName || `Event #${a.eventId}`) : `Event #${a.eventId}`;
  };

  // ── render ─────────────────────────────────────────────────────
  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Volunteer Check-In</Title>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconRefresh size={14} />}
          onClick={() => { loadAssignments(); loadEvents(); loadVolunteers(); }}
        >
          Refresh
        </Button>
      </Group>

      {/* GPS Status */}
      <Paper p="sm" radius="sm" withBorder mb="md">
        <Group gap="xs">
          <IconMapPin
            size={14}
            style={{ color: geoLocation ? '#07963F' : semantic.error }}
          />
          <Text size="xs" c="dimmed">
            {geoLocation
              ? `GPS: ${geoLocation.lat.toFixed(5)}, ${geoLocation.lng.toFixed(5)}`
              : geoError
                ? `GPS Error: ${geoError}`
                : 'Acquiring GPS...'}
          </Text>
          <Button size="compact-xs" variant="subtle" onClick={acquireGps}>
            Re-acquire
          </Button>
        </Group>
      </Paper>

      {/* Shift Code Entry */}
      <Paper p="md" radius="sm" withBorder mb="lg">
        <Text size="sm" fw={600} mb="xs">
          <IconBarcode size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          Enter Shift Code
        </Text>
        <Group gap="sm">
          <TextInput
            placeholder="e.g. ALPHA1"
            value={shiftCode}
            onChange={(e) => setShiftCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCodeSubmit(); }}
            size="sm"
            style={{ flex: 1, maxWidth: 240 }}
          />
          <Button size="sm" onClick={handleCodeSubmit}>
            Look Up
          </Button>
        </Group>
      </Paper>

      {/* Walk-in Check-In (directory volunteers without a pre-scheduled assignment) */}
      {canEdit && TABLES.people && (
        <Paper p="md" radius="sm" withBorder mb="lg" style={{ borderLeft: '3px solid #4B3FFF' }}>
          <Text size="sm" fw={600} mb="xs">
            <IconUsers size={14} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Walk-in Check-In
          </Text>
          <Text size="xs" c="dimmed" mb="sm">
            Check in a directory volunteer who doesn't have a pre-scheduled assignment.
          </Text>

          {/* Volunteer search */}
          {walkInPersonId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, fontSize: 14, marginBottom: 8 }}>
              <IconUsers size={13} color="#16a34a" />
              <span style={{ flex: 1, color: '#166534', fontWeight: 600 }}>{walkInPersonName}</span>
              <button onClick={() => { setWalkInPersonId(null); setWalkInPersonName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}><IconX size={12} /></button>
            </div>
          ) : (
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <TextInput
                placeholder="Search volunteer directory by name or email..."
                size="sm"
                value={walkInQuery}
                onChange={e => handleWalkInSearch(e.target.value)}
                leftSection={<IconSearch size={14} />}
              />
              {(walkInSearching || walkInResults.length > 0 || (walkInQuery && !walkInSearching)) && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e4e8', borderRadius: 4, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', zIndex: 100, marginTop: 2 }}>
                  {walkInSearching && <div style={{ padding: '8px 16px', fontSize: 12, color: '#888' }}>Searching…</div>}
                  {!walkInSearching && walkInResults.length === 0 && walkInQuery && <div style={{ padding: '8px 16px', fontSize: 12, color: '#888' }}>No volunteers found in directory</div>}
                  {walkInResults.map(p => (
                    <div key={p.id}
                      onClick={() => { setWalkInPersonId(p.id); setWalkInPersonName(`${p.firstName} ${p.lastName}`); setWalkInQuery(''); setWalkInResults([]); }}
                      style={{ padding: '8px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: 12 }}>
                      <div style={{ fontWeight: 600 }}>{p.firstName} {p.lastName}</div>
                      {p.email && <div style={{ color: '#6b7280' }}>{p.email}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Event selection */}
          <Select
            placeholder="Select event for check-in..."
            size="sm"
            data={events.filter(e => e.status === 'active' || e.status === 'open').map(e => {
              const incName = e.incidentId ? incidentMap.get(e.incidentId) : null;
              return {
                value: String(e.id),
                label: `${e.name || e.eventName}${e.eventDate ? ` — ${e.eventDate}` : ''}${incName ? ` [${incName}]` : ''}`,
              };
            })}
            value={walkInEventId}
            onChange={setWalkInEventId}
            mb="sm"
          />

          <Button
            size="sm"
            color="var(--color-success)"
            leftSection={<IconLogin size={14} />}
            onClick={handleWalkIn}
            loading={walkInProcessing}
            disabled={!walkInPersonId || !walkInEventId}
          >
            Check In Walk-in
          </Button>
        </Paper>
      )}

      {/* Ready to Check In */}
      <Group justify="space-between" align="center" mb="sm">
        <Title order={4}>
          <IconLogin size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Ready to Check In ({readyToCheckIn.length})
        </Title>
        {filterEventId && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', background: 'rgba(75,63,255,0.08)', border: '1px solid rgba(75,63,255,0.25)', borderRadius: 6, fontSize: 12 }}>
            <IconBarcode size={12} color="var(--color-primary-700)" />
            <span style={{ color: 'var(--color-primary-700)', fontWeight: 600 }}>Filtered to: {filterEventName}</span>
            <button
              onClick={() => { setFilterEventId(null); setFilterEventName(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, lineHeight: 1 }}
              title="Clear filter"
            >
              <IconX size={12} />
            </button>
          </div>
        )}
      </Group>
      {readyToCheckIn.length === 0 ? (
        <Paper p="lg" radius="sm" withBorder mb="lg">
          <Text ta="center" c="dimmed" size="sm">
            {filterEventId
              ? `No pending assignments for "${filterEventName}".`
              : 'No assignments ready for check-in today.'}
          </Text>
        </Paper>
      ) : (
        <Paper p={0} radius="sm" withBorder mb="lg">
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Volunteer</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Event</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Shift</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '8px 16px' }} />
                </tr>
              </thead>
              <tbody>
                {readyToCheckIn.map((a) => {
                  const canCheckIn = withinCheckInWindow(a.shiftDate, a.startTime);
                  return (
                    <tr key={a.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '8px 16px', fontWeight: 600 }}>{volName(a)}</td>
                      <td style={{ padding: '8px 16px' }}>{eventName(a)}</td>
                      <td style={{ padding: '8px 16px' }}>
                        {a.startTime || '--'} &ndash; {a.endTime || '--'}
                      </td>
                      <td style={{ padding: '8px 16px' }}>
                        <Badge color="var(--color-primary-700)" size="xs">{a.status}</Badge>
                      </td>
                      <td style={{ padding: '8px 16px' }}>
                        {canEdit && (
                          <Button
                            size="compact-xs"
                            color="var(--color-success)"
                            leftSection={<IconLogin size={14} />}
                            disabled={!canCheckIn}
                            loading={processing === a.id}
                            onClick={() => handleCheckIn(a)}
                            title={canCheckIn ? 'Check in now' : 'Check-in opens 1 hr before shift'}
                          >
                            Check In
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Paper>
      )}

      {/* Currently Checked In */}
      <Title order={4} mb="sm">
        <IconClock size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
        Currently Checked In ({currentlyCheckedIn.length})
      </Title>
      {currentlyCheckedIn.length === 0 ? (
        <Paper p="lg" radius="sm" withBorder mb="lg">
          <Text ta="center" c="dimmed" size="sm">
            No volunteers currently checked in.
          </Text>
        </Paper>
      ) : (
        <Paper p={0} radius="sm" withBorder mb="lg">
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Volunteer</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Event</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Checked In</th>
                  <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600 }}>Elapsed</th>
                  <th style={{ padding: '8px 16px' }} />
                </tr>
              </thead>
              <tbody>
                {currentlyCheckedIn.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                    <td style={{ padding: '8px 16px', fontWeight: 600 }}>{volName(a)}</td>
                    <td style={{ padding: '8px 16px' }}>{eventName(a)}</td>
                    <td style={{ padding: '8px 16px' }}>{fmtTime(a.checkIn)}</td>
                    <td style={{ padding: '8px 16px' }}>
                      <Badge color="#F86A0B" size="xs">{elapsedLabel(a.checkIn)}</Badge>
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      {canEdit && (
                        <Button
                          size="compact-xs"
                          color="var(--color-primary-700)"
                          leftSection={<IconLogout size={14} />}
                          loading={processing === a.id}
                          onClick={() => handleCheckOut(a)}
                        >
                          Check Out
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}

      {/* Recent Activity */}
      <Title order={4} mb="sm">Recent Activity Today</Title>
      {recentActivity.length === 0 ? (
        <Paper p="lg" radius="sm" withBorder>
          <Text ta="center" c="dimmed" size="sm">No check-in activity today.</Text>
        </Paper>
      ) : (
        <Paper p={0} radius="sm" withBorder>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          <Table striped highlightOnHover>
            <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
              <Table.Tr>
                <Table.Th>Volunteer</Table.Th>
                <Table.Th>Event</Table.Th>
                <Table.Th>In</Table.Th>
                <Table.Th>Out</Table.Th>
                <Table.Th>Hours</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {recentActivity.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td fw={600}>{volName(a)}</Table.Td>
                  <Table.Td>{eventName(a)}</Table.Td>
                  <Table.Td>{fmtTime(a.checkIn)}</Table.Td>
                  <Table.Td>{a.checkOut ? fmtTime(a.checkOut) : '--'}</Table.Td>
                  <Table.Td>{a.hours || (a.checkOut ? '--' : elapsedLabel(a.checkIn))}</Table.Td>
                  <Table.Td>
                    <Badge
                      size="xs"
                      color={a.checkOut ? 'var(--color-success)' : '#F86A0B'}
                    >
                      {a.checkOut ? 'completed' : 'active'}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </div>
        </Paper>
      )}
    </div>
  );
}
